import { Component, OnInit, ChangeDetectionStrategy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ChartDataset, ChartTypeRegistry, TooltipCallbacks, TooltipItem } from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { isSameDay, isSameMonth } from 'date-fns';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import { titleTooltipCallback } from 'src/app/tabs/forbruk/graphs/forbruk/charts/forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { AnalyseDayUsageChartComponent } from './chart/bar-chart.component';
import { MeteorologyObservations, MeteorologyForecast } from 'src/app/services/meteorologi/meteorologi.types';

@Component({
  selector: 'app-analyse-day-chart-usage',
  standalone: true,
  styleUrls: ['./analyse-day-chart-usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AnalyseDayUsageChartComponent],
  template: `
    @if (valgtAnlegg && selectedDate) {
      <p>
        Sammenlign ditt strømforbruk med utetemperaturen målt fra nærmeste værstasjon for inneværende måned.
      </p>
      <app-analyse-day-usage-chart
        chartId="myDaychartAnalyse-new"
        [dataSets]="dataSets"
        [labels]="labels"
        [title]="title"
        [tooltipCallbacks]="tooltipCallbacks"
        [xUnit]="'day'"
        [yUnit]="'kWh'"
        [yUnitRight]="'°C'"
        [showLegend]="showLegend"
        [showYTitle]="showYTitle"
        [tooltipFilter]="tooltipFilter"
      ></app-analyse-day-usage-chart>
    }
  `
})
export class AnalyseDayChartUsageComponent implements OnInit, OnChanges {
  @Input() valgtAnlegg: Anlegg;
  @Input() selectedDate: Date;
  @Input() title: string;
  @Input() showOutsideTemperature = false;
  @Input() meteorologyObservations: MeteorologyObservations;
  @Input() meteorologyForecast: MeteorologyForecast;

  dataSets: ChartDataset<'bar' | 'line'>[];
  labels: Date[];
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar' | 'line'>>;
  showLegend = true;
  useCurrentMonth: boolean;
  tooltipFilter: (item: TooltipItem<keyof ChartTypeRegistry>) => boolean;
  showYTitle = !this.showOutsideTemperature;
  customLabelTooltipCallback = (
    tooltipItem: TooltipItem<'bar' | 'line'>,
    unit: string[]
  ) => {
    const value = Number(tooltipItem.raw);
    if (value === 0 || isNaN(value) || tooltipItem.dataset.label == 'hide') {
      return undefined;
    }

    return `${tooltipItem.dataset.label}: ${value
      .toFixed(2)
      .toString()
      .replace('.', ',')} ${tooltipItem.dataset?.yAxisID && tooltipItem.dataset.yAxisID === 'y-axis-meteorology' ? unit[1] : unit[0]}`;
  };


  constructor() { }

  ngOnInit(): void {
    if (this.valgtAnlegg && this.selectedDate) {
      this.getChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.valgtAnlegg &&
      this.selectedDate &&
      hasChanged([changes?.valgtAnlegg])
    ) {
      this.getChartData();
    }
  }

  getChartData() {
    this.dataSets = [];
    let observedTemperaturesData = [];

    // Plusskunde produksjon
    const proddata = [];
    let plusskundedataFound = false;

    this.title = this.title || 'Forbruk/temperatur';

    this.tooltipCallbacks = {
      label: (tt) => this.customLabelTooltipCallback(tt, ['kWh', '°C']),
      title: (tt) =>
        titleTooltipCallback(tt, 'day', 'kWh', 'analyse_forbruk_tooltip_chart1')
    };
    this.tooltipFilter = (item) => {
      return item.raw !== 0;
    };

    // Generate labels for whole month
    const noOfDays = new Date(
      this.selectedDate?.getFullYear(),
      this.selectedDate.getMonth() + 1,
      0
    ).getDate();

    this.labels = [...Array(noOfDays).keys()].map(n => new Date(
      this.selectedDate?.getFullYear(), this.selectedDate.getMonth(), n + 1
    ));

    const forbruksData = this.labels.map((date) => {
      const value = this.valgtAnlegg.verdiList.find((v) =>
        isSameDay(new Date(v.tidspunkt), date)
      );

      if (value && isSameMonth(date, this.selectedDate)) {
        this.useCurrentMonth = true;

        if (this.valgtAnlegg.plusskunde) {
          proddata.push(value.plussverdi || 0);
          if (value.plussverdi > 0) plusskundedataFound = true;
        }

        return value.verdi || null;
      } else {
        if (this.valgtAnlegg.plusskunde) proddata.push(null);
        return null;
      }
    });

    if (this.useCurrentMonth) {
      this.dataSets.push({
        type: 'line',
        data: forbruksData,
        label: 'Ditt forbruk',
        backgroundColor: '#0a66ff',
        borderColor: '#0a66ff',
        pointStyle: 'rect',
        order: 0,
        borderWidth: 2,
      });

      if (plusskundedataFound) {
        this.dataSets.push({
          type: 'line',
          data: proddata,
          label: 'Overskudd',
          backgroundColor: '#26b41b66',
          borderColor: '#26b41b',
          order: 1,
          borderWidth: 2,
          pointStyle: 'rect',
        });
      }
    }

    const isForecastStyle = (ctx, value) =>
      forecastIndexStart > -1 && ctx.p0DataIndex + 1 >= forecastIndexStart ? value : undefined; 
    let forecastIndexStart = -1;

    if (this.useCurrentMonth && this.showOutsideTemperature && this.meteorologyObservations?.parts) {
      const today = new Date();
      // 5 days into the future
      const cutDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5);

      observedTemperaturesData = this.labels.map((date, i) => {
        const value = this.meteorologyObservations.parts.find((v) => {
          // check if period is in valid format
          if (!(v.period?.toString().length === 8)) return false;

          const year = +v.period.toString().substring(0, 4);
          const month = +v.period.toString().substring(4, 6);
          const day = +v.period.toString().substring(6, 8);
          const periodeDate = new Date(year, month - 1, day);

          return periodeDate.getTime() === date.getTime();
        });
        const forecast = this.meteorologyForecast.parts.find((v) => {
          if (!(v.period?.toString().length === 8)) return false;

          const year = +v.period.toString().substring(0, 4);
          const month = +v.period.toString().substring(4, 6);
          const day = +v.period.toString().substring(6, 8);
          const periodeDate = new Date(year, month - 1, day);

          return periodeDate.getTime() === date.getTime();
        });

        if (value?.air_temperature && isSameMonth(date, this.selectedDate)) {
          return value.air_temperature;
        } else if (isSameMonth(date, this.selectedDate) && date <= cutDate) {
          // Forecast
          if (forecastIndexStart < 0) forecastIndexStart = i;
          return forecast?.air_temperature;
        } else {
          return null;
        }
      });

      this.dataSets.push({
        type: 'line',
        yAxisID: 'y-axis-meteorology',
        data: observedTemperaturesData,
        label: 'Temperatur/værmelding', 
        backgroundColor: '#4d284ccc',
        borderColor: '#4d284ccc',
        pointStyle: 'line',
        pointRadius: 1,
        borderWidth: 2,
        order: 2,
        segment: {
          borderDash: ctx => isForecastStyle(ctx, [4, 2]), 
        },
      });
    }

    // if (this.useCurrentMonth && this.showOutsideTemperature && this.meteorologyForecast?.parts) {
    //   const today = new Date();
    //   // 5 days into the future
    //   const cutDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 5);
    //   const forecastTemperatureDate = this.labels.map((date, i) => {
    //     if (observedTemperaturesData[i] || date > cutDate) {
    //       return null;
    //     }
    //
    //     const value = this.meteorologyForecast.parts.find((v) => {
    //       if (!(v.period?.toString().length === 8)) return false;
    //
    //       const year = +v.period.toString().substring(0, 4);
    //       const month = +v.period.toString().substring(4, 6);
    //       const day = +v.period.toString().substring(6, 8);
    //       const periodeDate = new Date(year, month - 1, day);
    //
    //       return periodeDate.getTime() === date.getTime();
    //     });
    //
    //     if (value?.air_temperature && isSameMonth(date, this.selectedDate)) {
    //       return value.air_temperature;
    //     } else {
    //       return null;
    //     }
    //   });
    //
    //   this.dataSets.push({
    //     type: 'line',
    //     yAxisID: 'y-axis-meteorology',
    //     data: forecastTemperatureDate,
    //     label: 'Værmelding',
    //     backgroundColor: '#4d284ccc',
    //     borderColor: '#4d284ccc',
    //     pointStyle: 'line',
    //     borderDash: [4, 2],
    //     fill: false,
    //     pointRadius: 1,
    //     borderWidth: 2,
    //     order: 3,
    //   });
    // }

    this.showLegend = this.showOutsideTemperature || plusskundedataFound;
  }
}
